import { useCallback, useEffect, useRef, useState } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import type { ArcgisMapView } from './arcgisView';
import {
  symbolToPictureMarkerMap,
  symbolToGeometryTypeMap,
  isFillSymbol,
  isSimpleLineSymbol,
  isSimpleMarkerSymbol,
  type SymbolGeometryType,
  type UscgSymbol,
} from './uscgSymbology';
import { createSimpleMarkerSymbol } from './simpleMarkerSymbol';
import { WGS84_SPATIAL_REFERENCE } from './spatialReference';
import {
  createPolylineFromPoints,
  createPolylineSymbol,
} from './polylineGraphics';
import type { DrawingUpdate, PointGeometry } from './constants';
import { useArcgisSketchLayer } from './useArcgisSketchLayer';
import { v4 as uuidv4 } from 'uuid';
import { useLatest } from 'react-use';
import { colord } from 'colord';
import { arcgisGeometryToGeoJson } from './arcgisGeometryToGeoJson';
import { normalizeOpacity } from './normalizeOpacity';
import type { Geometry } from 'geojson';
import {
  decorateMapGraphic,
  type MapFeatureDetails,
} from './locationPopup';

type SymbolPoint = PointGeometry & {
  id: string;
  order: number;
};

type SymbolLine = {
  id: string;
  points: SymbolPoint[];
};

const DEFAULT_POLYGON_COLOR_HEX = '#1e40af';

function openPolygonRing(ring: number[][]) {
  if (ring.length < 2) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (
    first &&
    last &&
    Math.abs((first[0] ?? 0) - (last[0] ?? 0)) < 1e-6 &&
    Math.abs((first[1] ?? 0) - (last[1] ?? 0)) < 1e-6
  ) {
    return ring.slice(0, -1);
  }
  return ring;
}

const polygonFillSymbol: __esri.SimpleFillSymbolProperties & {
  type: 'simple-fill';
} = {
  type: 'simple-fill',
  color: [30, 64, 175, 0.25],
  outline: {
    color: [30, 58, 138, 1],
    width: 2,
  },
};

const createPolygonFillSymbol = (
  colorHex?: string,
  opacity?: number
): __esri.SimpleFillSymbolProperties & { type: 'simple-fill' } => {
  if (!colorHex) return polygonFillSymbol;

  const parsedColor = colord(colorHex);
  if (!parsedColor.isValid()) {
    return polygonFillSymbol;
  }

  const { r, g, b } = parsedColor.toRgb();

  return {
    type: 'simple-fill',
    color: [r, g, b, normalizeOpacity(opacity)],
    outline: {
      color: [r, g, b, 1],
      width: 2,
    },
  };
};

/** Map drawing hook for USCG symbols. Supports single point and line symbols. */
export const useUscgSymbolDrawing = ({
  mapView,
}: {
  mapView: ArcgisMapView | null;
}) => {
  const [geometryType, setGeometryType] = useState<SymbolGeometryType | null>(
    null
  );
  const [points, setPoints] = useState<SymbolPoint[]>([]);
  const [line, setLine] = useState<SymbolLine | null>(null);
  const [polygon, setPolygon] = useState<Geometry | null>(null);
  const [polygonColorHex, setPolygonColorHex] = useState(
    DEFAULT_POLYGON_COLOR_HEX
  );
  const [polygonOpacity, setPolygonOpacity] = useState(normalizeOpacity());

  const pendingLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: 'Pending Drawing Layer' })
  );

  const latestGeometryType = useLatest(geometryType);
  const symbolKeyRef = useRef<UscgSymbol | null>(null);
  const polygonDrawSeq = useRef(0);

  const onDrawingUpdate = useCallback(
    (update: DrawingUpdate) => {
      setPoints((prev) => {
        const currentType = latestGeometryType.current;

        if (update.kind === 'polyline') {
          return update.points.map((geometry, index) => ({
            id: uuidv4(),
            longitude: geometry.longitude,
            latitude: geometry.latitude,
            order: index,
          }));
        }

        const newPoint: SymbolPoint = {
          id: uuidv4(),
          longitude: update.point.longitude,
          latitude: update.point.latitude,
          order: prev.length,
        };

        if (currentType === 'single') {
          return [newPoint];
        }

        if (currentType === 'line') {
          return [...prev, newPoint];
        }

        return prev;
      });
    },
    [latestGeometryType]
  );

  const handleCreateGeometry = useCallback(
    async ({
      geometry,
      sketchViewModel,
    }: {
      geometry: __esri.Geometry;
      sketchViewModel: __esri.SketchViewModel;
    }) => {
      if (geometry.type === 'point') {
        const pointGeom = geometry as __esri.Point;
        const longitude = pointGeom.longitude ?? pointGeom.x;
        const latitude = pointGeom.latitude ?? pointGeom.y;

        if (typeof longitude === 'number' && typeof latitude === 'number') {
          onDrawingUpdate({
            kind: 'point',
            point: { longitude, latitude },
          });
        }

        // For multi-point geometries (for example line-style symbols), immediately
        // start a new point draw so the user can keep clicking until drawing stops.
        if (latestGeometryType.current !== 'single') {
          sketchViewModel.create('point');
        }
      } else if (geometry.type === 'polyline') {
        const polyline = geometry as __esri.Polyline;
        const spatialRef = polyline.spatialReference;
        const path = polyline.paths[0] ?? [];

        const nextPoints: PointGeometry[] = path.map(([x, y]) => {
          let longitude = x;
          let latitude = y;

          const wkid = spatialRef?.wkid;
          // If the map is in Web Mercator (102100/3857), convert meter-based
          // map units into geographic lon/lat before reporting to consumers.
          if (wkid === 102100 || wkid === 3857) {
            const [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
            longitude = lng;
            latitude = lat;
          }

          return { longitude, latitude };
        });

        if (nextPoints.length > 0) {
          onDrawingUpdate({
            kind: 'polyline',
            points: nextPoints,
          });
        }
      } else if (geometry.type === 'polygon') {
        const polygonGeom = geometry as __esri.Polygon;
        const wkid = polygonGeom.spatialReference?.wkid;
        const geographic = (polygonGeom.rings?.[0] ?? []).map(([x, y]) => {
          if (wkid === 102100 || wkid === 3857) {
            const [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
            return [lng, lat];
          }
          return [x, y];
        });
        const sketched = openPolygonRing(geographic);
        setPoints(
          sketched.map((coord, index) => ({
            id: uuidv4(),
            longitude: coord[0] ?? 0,
            latitude: coord[1] ?? 0,
            order: index,
          }))
        );
        if (sketched.length >= 3 && sketched[0]) {
          setPolygon({
            type: 'Polygon',
            coordinates: [[...sketched, sketched[0]]],
          });
        } else {
          setPolygon(null);
        }

        const seq = ++polygonDrawSeq.current;
        const geoJson = await arcgisGeometryToGeoJson(
          geometry as __esri.GeometryUnion
        );
        if (seq !== polygonDrawSeq.current) return;

        const ring =
          geoJson.type === 'Polygon' ? (geoJson.coordinates[0] ?? []) : [];
        const open = openPolygonRing(ring);
        if (open.length > 0) {
          setPoints(
            open.map((coord, index) => ({
              id: uuidv4(),
              longitude: coord[0] ?? 0,
              latitude: coord[1] ?? 0,
              order: index,
            }))
          );
        }
        if (geoJson.type === 'Polygon') setPolygon(geoJson);
      }
    },
    [onDrawingUpdate, latestGeometryType]
  );

  const {
    drawingLayerRef: drawLayerRef,
    sketchViewModelRef,
    clearDrawingLayer,
    cancelActiveDrawing,
  } = useArcgisSketchLayer({
    mapView,
    layerTitle: 'Drawing Layer',
    onCreateComplete: handleCreateGeometry,
    onUpdateComplete: handleCreateGeometry,
  });

  useEffect(() => {
    if (!mapView?.map) return;
    const map = mapView.map;
    const pendingLayer = pendingLayerRef.current;
    map.add(pendingLayer);

    return () => {
      map.remove(pendingLayer);
    };
  }, [mapView]);

  const stopDrawing = useCallback(() => {
    cancelActiveDrawing();
    clearDrawingLayer();
  }, [cancelActiveDrawing, clearDrawingLayer]);

  const setPolygonDrawingColor = useCallback((colorHex: string) => {
    setPolygonColorHex(colorHex);
  }, []);

  const setPolygonDrawingOpacity = useCallback((opacity?: number) => {
    setPolygonOpacity(normalizeOpacity(opacity));
  }, []);

  useEffect(() => {
    const symbolKey = symbolKeyRef.current;
    if (symbolKey === 'customPoint') {
      const marker = createSimpleMarkerSymbol(
        symbolKey,
        polygonColorHex,
        polygonOpacity
      );
      if (sketchViewModelRef.current) {
        sketchViewModelRef.current.pointSymbol = marker;
      }
      drawLayerRef.current.graphics.forEach((graphic: Graphic) => {
        if (graphic.geometry?.type === 'point') {
          graphic.symbol = marker;
        }
      });
    }

    if (geometryType !== 'polygon') return;

    const polygonSymbol = createPolygonFillSymbol(
      polygonColorHex,
      polygonOpacity
    );
    if (sketchViewModelRef.current) {
      sketchViewModelRef.current.polygonSymbol = polygonSymbol;
    }

    drawLayerRef.current.graphics.forEach((graphic: Graphic) => {
      if (graphic.geometry?.type === 'polygon') {
        graphic.symbol = polygonSymbol;
      }
    });
  }, [
    drawLayerRef,
    geometryType,
    polygonColorHex,
    polygonOpacity,
    sketchViewModelRef,
  ]);

  const startDrawing = useCallback(
    (symbolKey: UscgSymbol) => {
      const type = symbolToGeometryTypeMap[symbolKey];
      const symbol = symbolToPictureMarkerMap[symbolKey];
      if (!sketchViewModelRef.current) return;

      const sketchViewModel = sketchViewModelRef.current;
      symbolKeyRef.current = symbolKey;

      setGeometryType(type);
      setPoints([]);
      setLine(null);
      setPolygon(null);

      stopDrawing();

      // Reset create options on every symbol change so mode from a previous
      // tool (for example, oil spill freehand) does not leak into the next one.
      sketchViewModel.defaultCreateOptions = {
        ...sketchViewModel.defaultCreateOptions,
        mode: 'click',
      };

      if (symbolKey === 'oilSpill') {
        sketchViewModel.polylineSymbol = createPolylineSymbol('oilSpill');
        sketchViewModel.defaultCreateOptions = {
          ...sketchViewModel.defaultCreateOptions,
          mode: 'freehand',
        };
        sketchViewModel.create('polyline');
        return;
      }

      if (type === 'polygon' || isFillSymbol(symbolKey)) {
        sketchViewModel.polygonSymbol = createPolygonFillSymbol(
          polygonColorHex,
          polygonOpacity
        );
        sketchViewModel.create('polygon');
        return;
      }

      if (isSimpleLineSymbol(symbolKey)) {
        sketchViewModel.polylineSymbol = createPolylineSymbol(symbolKey);
        sketchViewModel.create('polyline');
        return;
      }

      if (isSimpleMarkerSymbol(symbolKey)) {
        sketchViewModel.pointSymbol = createSimpleMarkerSymbol(
          symbolKey,
          symbolKey === 'customPoint' ? polygonColorHex : undefined,
          symbolKey === 'customPoint' ? polygonOpacity : 1
        );
        sketchViewModel.create('point');
        return;
      }

      if (!symbol) return;

      sketchViewModel.pointSymbol = symbol;
      sketchViewModel.create('point');
    },
    [polygonColorHex, polygonOpacity, sketchViewModelRef, stopDrawing]
  );

  const resetDrawing = useCallback(() => {
    setGeometryType(null);
    setPoints([]);
    setLine(null);
    setPolygon(null);
    stopDrawing();
  }, [stopDrawing]);

  const addPendingPoint = useCallback(
    ({
      symbolKey,
      point,
      pendingGraphicId,
      colorHex,
      opacity,
      details,
    }: {
      symbolKey: UscgSymbol;
      point: PointGeometry;
      pendingGraphicId: string;
      colorHex?: string;
      opacity?: number;
      details?: MapFeatureDetails;
    }) => {
      const pendingLayer = pendingLayerRef.current;
      const pictureMarkerSymbol = isSimpleMarkerSymbol(symbolKey)
        ? createSimpleMarkerSymbol(symbolKey, colorHex, opacity)
        : symbolToPictureMarkerMap[symbolKey];
      if (!pictureMarkerSymbol) return;

      const geometry = new Point({
        longitude: point.longitude,
        latitude: point.latitude,
        spatialReference: WGS84_SPATIAL_REFERENCE,
      });

      const graphic = new Graphic({
        geometry,
        symbol: pictureMarkerSymbol,
        attributes: { pendingGraphicId },
      });
      decorateMapGraphic(graphic, details);
      pendingLayer.add(graphic);
    },
    []
  );

  const addPendingPolyline = useCallback(
    ({
      symbolKey,
      points: nextPoints,
      pendingGraphicId,
      details,
    }: {
      symbolKey: UscgSymbol;
      points: PointGeometry[];
      pendingGraphicId: string;
      details?: MapFeatureDetails;
    }) => {
      if (nextPoints.length < 2) return;
      const graphic = new Graphic({
        geometry: createPolylineFromPoints(nextPoints),
        symbol: createPolylineSymbol(symbolKey),
        attributes: { pendingGraphicId },
      });
      decorateMapGraphic(graphic, details);
      pendingLayerRef.current.add(graphic);
    },
    []
  );

  const addPendingPolygon = useCallback(
    ({
      geometry,
      colorHex,
      opacity,
      pendingGraphicId,
      details,
    }: {
      geometry: Geometry;
      colorHex?: string;
      opacity?: number;
      pendingGraphicId: string;
      details?: MapFeatureDetails;
    }) => {
      if (geometry.type !== 'Polygon') return;

      const graphic = new Graphic({
        geometry: new Polygon({
          rings: geometry.coordinates,
          spatialReference: WGS84_SPATIAL_REFERENCE,
        }),
        symbol: createPolygonFillSymbol(colorHex, opacity),
        attributes: { pendingGraphicId },
      });
      decorateMapGraphic(graphic, details);
      pendingLayerRef.current.add(graphic);
    },
    []
  );

  const clearPendingGraphics = useCallback(() => {
    pendingLayerRef.current.removeAll();
  }, []);

  const removePendingGraphicsByPendingGraphicId = useCallback(
    (pendingGraphicId: string) => {
      const toRemove: Graphic[] = [];
      pendingLayerRef.current.graphics.forEach((graphic: Graphic) => {
        const attrs = graphic.attributes as Record<string, unknown> | undefined;
        if (attrs?.pendingGraphicId === pendingGraphicId) {
          toRemove.push(graphic);
        }
      });
      for (const graphic of toRemove) {
        pendingLayerRef.current.remove(graphic);
      }
    },
    []
  );

  const connectDrawingPoints = useCallback(
    (symbolKey: UscgSymbol) => {
      if (!geometryType || points.length < 2) return;

      const sortedPoints = [...points].sort((a, b) => a.order - b.order);
      setLine({
        id: uuidv4(),
        points: sortedPoints,
      });

      if (geometryType === 'line') {
        drawLayerRef.current.add(
          new Graphic({
            geometry: createPolylineFromPoints(sortedPoints),
            symbol: createPolylineSymbol(symbolKey),
          })
        );
      }
    },
    [geometryType, points, drawLayerRef]
  );

  const applyTypedCoordinates = useCallback(
    (coords: PointGeometry[], details?: MapFeatureDetails) => {
      const symbolKey = symbolKeyRef.current;
      const type = latestGeometryType.current;
      if (!symbolKey || !type) return;

      cancelActiveDrawing();
      clearDrawingLayer();

      const addDrawn = (graphic: Graphic) => {
        decorateMapGraphic(graphic, details);
        drawLayerRef.current.add(graphic);
      };

      const next = coords.map((coord, index) => ({
        id: uuidv4(),
        longitude: coord.longitude,
        latitude: coord.latitude,
        order: index,
      }));
      setPoints(next);

      if (type === 'polygon') {
        if (next.length < 3) {
          setPolygon(null);
          setLine(null);
          return;
        }
        const ring = next.map((point) => [point.longitude, point.latitude]);
        const closed = [...ring, ring[0]];
        const geo: Geometry = { type: 'Polygon', coordinates: [closed] };
        setPolygon(geo);
        setLine(null);
        addDrawn(
          new Graphic({
            geometry: new Polygon({
              rings: [closed],
              spatialReference: WGS84_SPATIAL_REFERENCE,
            }),
            symbol: createPolygonFillSymbol(polygonColorHex, polygonOpacity),
          })
        );
        return;
      }

      if (type === 'line') {
        setPolygon(null);
        if (next.length < 2) {
          setLine(null);
          return;
        }
        setLine({ id: uuidv4(), points: next });
        addDrawn(
          new Graphic({
            geometry: createPolylineFromPoints(next),
            symbol: createPolylineSymbol(symbolKey),
          })
        );
        return;
      }

      setLine(null);
      setPolygon(null);
      const point = next[next.length - 1];
      if (!point) return;
      const marker = isSimpleMarkerSymbol(symbolKey)
        ? createSimpleMarkerSymbol(
            symbolKey,
            symbolKey === 'customPoint' ? polygonColorHex : undefined,
            symbolKey === 'customPoint' ? polygonOpacity : 1
          )
        : symbolToPictureMarkerMap[symbolKey];
      if (!marker) return;
      addDrawn(
        new Graphic({
          geometry: new Point({
            longitude: point.longitude,
            latitude: point.latitude,
            spatialReference: WGS84_SPATIAL_REFERENCE,
          }),
          symbol: marker,
        })
      );
    },
    [
      cancelActiveDrawing,
      clearDrawingLayer,
      drawLayerRef,
      latestGeometryType,
      polygonColorHex,
      polygonOpacity,
    ]
  );

  const getDrawnGraphic = useCallback((): Graphic | null => {
    let graphic: Graphic | null = null;
    drawLayerRef.current.graphics.forEach((item: Graphic) => {
      graphic = item;
    });
    return graphic;
  }, [drawLayerRef]);

  const beginEditingLocation = useCallback(() => {
    const sketch = sketchViewModelRef.current;
    const graphic = getDrawnGraphic();
    if (!sketch || !graphic?.geometry) return false;
    if (sketch.state === 'active') return false;

    const tool = graphic.geometry.type === 'point' ? 'move' : 'reshape';
    void sketch.update(graphic, { tool });
    return true;
  }, [getDrawnGraphic, sketchViewModelRef]);

  const describeDrawnGraphic = useCallback(
    (details?: MapFeatureDetails) => {
      const graphic = getDrawnGraphic();
      if (!graphic) return;
      decorateMapGraphic(graphic, details);
    },
    [getDrawnGraphic]
  );

  return {
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    stopDrawing: resetDrawing,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    symbolDrawingState: {
      geometryType,
      points,
      line,
      polygon,
    },
    connectDrawingPoints,
    applyTypedCoordinates,
    resetDrawing,
    getDrawnGraphic,
    beginEditingLocation,
    describeDrawnGraphic,
  };
};

export type UscgSymbolDrawingReturnType = ReturnType<
  typeof useUscgSymbolDrawing
>;
